"use client";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  HOME_HERO_REVEAL_EVENT,
  HOME_HERO_REVEAL_SCROLL_PX,
} from "@/lib/home-hero-intro";
import { useScrollToTop } from "@/hooks/use-scroll-to-top";
import { hashHref } from "@/lib/hash-href";


const navLinkStaticBase =
  "text-xs font-medium uppercase tracking-tight transition-opacity transition-colors hover:opacity-80 sm:text-xs md:text-base md:font-normal";

type MenuLink = { label: string; href: string; mobileOnly?: boolean };

const WHAT_WE_DO_SUBMENU: MenuLink[] = [
  { label: "Brands & Design", href: "/concept-and-design" },
  { label: "Web Design", href: "/website-design" },
  { label: "Digital Marketing", href: "/digital-marketing" },
];

/** Overlay menu links; `mobileOnly` items are hidden from md+ in the hamburger panel. */
const MENU_LINKS: MenuLink[] = [
  { label: "What we've done", href: "/case-studies", mobileOnly: true },
  { label: "What's new", href: "/blog" },
  { label: "Who we are", href: "/who-we-are" },
  { label: "Want a cool job", href: "/careers" },
  { label: "Contact", href: "/contact" },
];

/** Routes that use a white bar under the fixed header (dark nav text, inverted logo), same as blog. */
function usesLightHeaderBackground(pathname: string) {
  return (
    pathname === "/blog" ||
    pathname.startsWith("/blog/") ||
    pathname === "/case-studies" ||
    pathname.startsWith("/case-studies/") ||
    pathname === "/terms" ||
    pathname === "/privacy" ||
    pathname === "/contact"
  );
}

function subscribeToNothing() {
  return () => {};
}

export function Header() {
  const pathname = usePathname();
  const isHome = pathname === "/";
  const isLightHeaderRoute = usesLightHeaderBackground(pathname);
  const headerRef = useRef<HTMLElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const circleRef = useRef<HTMLDivElement | null>(null);
  const menuNavRef = useRef<HTMLElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [whatWeDoOpen, setWhatWeDoOpen] = useState(false);
  const [whatWeDoAccordionOpen, setWhatWeDoAccordionOpen] = useState(false);
  const whatWeDoWrapRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = useScrollToTop();
  const [circleOrigin, setCircleOrigin] = useState({ x: 0, y: 0 });
  /**
   * After hydration, true on the client; always false during SSR / hydrate (avoids pathname-dependent
   * nav `className` mismatches on e.g. /careers).
   */
  const hasMounted = useSyncExternalStore(
    subscribeToNothing,
    () => true,
    () => false,
  );

  useEffect(() => {
    const thresholdPx = 850;

    const update = () => {
      const nextIsScrolled = window.scrollY >= thresholdPx;
      setIsScrolled((prevIsScrolled) => (prevIsScrolled === nextIsScrolled ? prevIsScrolled : nextIsScrolled));
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  /** Blog and legal pages use a light background under the fixed header — match “scrolled” chrome (black text + inverted logo). */
  const useDarkHeaderChrome = isScrolled || isLightHeaderRoute;

  const navLinkClass = useMemo(() => {
    /** Orange hero: orange hover on white links is illegible — use black on careers until header goes light (scroll). */
    const careersOrangeHeroHover =
      hasMounted && pathname === "/careers" && !useDarkHeaderChrome;
    const hoverColorClass = careersOrangeHeroHover ? "hover:text-black" : "hover:text-[#ff5c00]";
    if (menuOpen) return `${navLinkStaticBase} ${hoverColorClass} text-white`;
    return `${navLinkStaticBase} ${hoverColorClass} ${useDarkHeaderChrome ? "text-black" : "text-white"}`;
  }, [hasMounted, menuOpen, pathname, useDarkHeaderChrome]);

  const focusOutlineClass = useMemo(() => {
    if (menuOpen) return "focus-visible:outline-white";
    return useDarkHeaderChrome ? "focus-visible:outline-black" : "focus-visible:outline-white";
  }, [menuOpen, useDarkHeaderChrome]);
  const logoInvertClass = useMemo(() => {
    if (menuOpen) return "";
    return useDarkHeaderChrome ? "invert" : "";
  }, [menuOpen, useDarkHeaderChrome]);

  const openMenu = useCallback(() => {
    const rect = menuButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setCircleOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
    setMenuOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setWhatWeDoAccordionOpen(false);
    const circle = circleRef.current;
    const nav = menuNavRef.current;
    if (!circle) {
      setMenuOpen(false);
      return;
    }
    const links = nav?.querySelectorAll<HTMLElement>("[data-menu-link]") ?? [];

    gsap
      .timeline({
        onComplete: () => setMenuOpen(false),
      })
      .to(links, {
        opacity: 0,
        y: 20,
        duration: 0.22,
        stagger: 0.03,
        ease: "power2.in",
      })
      .to(
        circle,
        {
          scale: 0,
          duration: 0.55,
          ease: "power3.in",
        },
        0
      );
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (!target) return false;

    const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 96;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return true;
  }, []);

  const handleSectionLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      if (pathname !== "/") return;
      e.preventDefault();
      window.history.replaceState(null, "", `/#${sectionId}`);
      scrollToSection(sectionId);
    },
    [pathname, scrollToSection]
  );

  useGSAP(
    () => {
      if (!menuOpen) return;
      const circle = circleRef.current;
      const nav = menuNavRef.current;
      if (!circle || !nav) return;

      const linkEls = nav.querySelectorAll<HTMLElement>("[data-menu-link]");
      gsap.killTweensOf([circle, ...linkEls]);

      gsap.set(circle, { scale: 0, transformOrigin: "center center" });
      gsap.set(linkEls, { opacity: 0, y: 28 });

      gsap
        .timeline()
        .to(circle, {
          scale: 1,
          duration: 0.75,
          ease: "power3.inOut",
        })
        .to(
          linkEls,
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.07,
            ease: "power2.out",
          },
          "-=0.38"
        );
    },
    { dependencies: [menuOpen] }
  );

  useGSAP(
    () => {
      const el = headerRef.current;
      if (!el) return;

      if (pathname !== "/") {
        gsap.set(el, { autoAlpha: 1 });
        return;
      }

      gsap.set(el, { autoAlpha: 0 });

      if (window.scrollY > HOME_HERO_REVEAL_SCROLL_PX) {
        gsap.set(el, { autoAlpha: 1 });
        return;
      }

      const onReveal = () => {
        gsap.to(el, { autoAlpha: 1, duration: 0.35, ease: "power2.out" });
      };
      window.addEventListener(HOME_HERO_REVEAL_EVENT, onReveal);
      return () => window.removeEventListener(HOME_HERO_REVEAL_EVENT, onReveal);
    },
    { dependencies: [pathname] }
  );

  useEffect(() => {
    const root = whatWeDoWrapRef.current;
    if (!root) return;
    const onFocusIn = () => setWhatWeDoOpen(true);
    const onFocusOut = (e: FocusEvent) => {
      const rt = e.relatedTarget as Node | null;
      if (rt && root.contains(rt)) return;
      setWhatWeDoOpen(false);
    };
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    return () => {
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", onFocusOut);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (pathname !== "/") return;
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;

    const id = decodeURIComponent(hash);
    const rafId = window.requestAnimationFrame(() => {
      scrollToSection(id);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [pathname, scrollToSection]);

  return (
    <>
    <header
      ref={headerRef}
      className={`fixed inset-x-0 top-0 transition-colors duration-300 ease-in-out ${
        menuOpen
          ? "z-210 bg-transparent"
          : useDarkHeaderChrome
            ? "z-50 bg-white"
            : "z-50 bg-transparent"
      }`}
      style={isHome ? { opacity: 0, visibility: "hidden" } : undefined}
    >
      <div
        className="site-content flex h-16 items-center justify-between md:h-24"
      >
        <Link
          href="/"
          className={`relative inline-flex shrink-0 items-center no-underline focus-visible:outline-2 focus-visible:outline-offset-4 ${focusOutlineClass}`}
          onClick={(e) => {
            if (!isHome) return;
            e.preventDefault();
            scrollToTop();
          }}
        >
          <Image
            src="/images/hiho-logo-rev.svg"
            alt="HiHo"
            width={810}
            height={418}
            className={`h-8 w-auto md:h-14 transition-[filter] duration-300 ease-in-out ${logoInvertClass}`}
            priority
          />
        </Link>

        <nav
          className="flex items-center gap-4 sm:gap-6 md:gap-8 lg:gap-10"
          aria-label="Primary"
        >
          {!menuOpen ? (
            <div className="hidden md:flex md:items-center md:gap-8 lg:gap-10">
              <Link href="/case-studies" className={navLinkClass}>
                What We&apos;ve Done
              </Link>
              <div
                ref={whatWeDoWrapRef}
                className="relative"
                onMouseEnter={() => setWhatWeDoOpen(true)}
                onMouseLeave={() => setWhatWeDoOpen(false)}
              >
                <Link
                  href={hashHref(pathname, "what-we-do")}
                  className={navLinkClass}
                  aria-expanded={whatWeDoOpen}
                  aria-haspopup="true"
                  aria-controls="what-we-do-submenu"
                  onClick={(e) => handleSectionLinkClick(e, "what-we-do")}
                >
                  What We Do
                </Link>
                <div
                  className={`absolute left-0 top-full z-50 pt-2 transition-all duration-200 ease-out -ml-4 ${
                    whatWeDoOpen ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0"
                  }`}
                  id="what-we-do-submenu"
                  role="presentation"
                >
                  <ul
                    role="menu"
                    aria-label="What we do"
                    className={`min-w-60 py-2 ${
                      useDarkHeaderChrome
                        ? "bg-white shadow-lg"
                        : "bg-[#14120f]/95 shadow-lg backdrop-blur-sm"
                    }`}
                  >
                    {WHAT_WE_DO_SUBMENU.map((item) => (
                      <li key={item.href} role="none">
                        <Link
                          role="menuitem"
                          href={item.href}
                          tabIndex={whatWeDoOpen ? 0 : -1}
                          className={`block px-4 py-2.5 ${navLinkStaticBase} ${
                            useDarkHeaderChrome
                              ? "text-black hover:bg-black/4 hover:text-[#ff5c00]"
                              : "text-white/95 hover:bg-white/10 hover:text-[#ff5c00]"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Link href="/contact" className={navLinkClass}>
                Contact
              </Link>
            </div>
          ) : null}
          <button
            ref={menuButtonRef}
            type="button"
            className={`ml-2 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#ff703d] transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 md:ml-3 md:size-12 ${focusOutlineClass}`}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? "site-menu" : undefined}
            onClick={() => (menuOpen ? closeMenu() : openMenu())}
          >
            <span className="relative flex h-5 w-5 items-center justify-center">
              <span
                className={`absolute block h-0.5 w-5 rounded-full bg-black transition-transform duration-300 ease-out ${
                  menuOpen ? "translate-y-0 rotate-45" : "-translate-y-[5px]"
                }`}
              />
              <span
                className={`absolute block h-0.5 w-5 rounded-full bg-black transition-transform duration-300 ease-out ${
                  menuOpen ? "translate-y-0 -rotate-45" : "translate-y-[5px]"
                }`}
              />
            </span>
          </button>
        </nav>
      </div>
    </header>

    {menuOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 z-200 flex min-h-full w-full items-center justify-center overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            id="site-menu"
            onClick={closeMenu}
          >
            <div
              ref={circleRef}
              className="pointer-events-none absolute rounded-full bg-[#14120f]"
              style={{
                left: circleOrigin.x,
                top: circleOrigin.y,
                width: "320vmax",
                height: "320vmax",
                marginLeft: "-160vmax",
                marginTop: "-160vmax",
                transform: "scale(0)",
              }}
              aria-hidden
            />
            <nav
              ref={menuNavRef}
              className="relative z-10 flex w-full max-w-[min(100%,96rem)] flex-col items-start gap-4 overflow-x-auto overflow-y-visible px-6 py-24 sm:gap-6 sm:px-10 md:gap-8 md:px-16 lg:gap-10"
              aria-label="Primary navigation"
              onClick={(e) => e.stopPropagation()}
            >
              {MENU_LINKS.filter((item) => item.mobileOnly).map((item) => (
                <Link
                  key={item.label}
                  data-menu-link
                  href={item.href.startsWith("/") ? item.href : hashHref(pathname, item.href)}
                  className="block w-max max-w-full whitespace-normal text-2xl font-medium uppercase tracking-tight text-white/95 transition-colors hover:text-[#ff5c00] sm:whitespace-nowrap sm:text-4xl md:hidden"
                  onClick={(e) => {
                    if (!item.href.startsWith("/")) {
                      handleSectionLinkClick(e, item.href);
                    }
                    closeMenu();
                  }}
                >
                  {item.label}
                </Link>
              ))}
              <div className="flex w-full min-w-0 flex-col items-start gap-0">
                <button
                  type="button"
                  data-menu-link
                  id="site-menu-what-we-do-trigger"
                  aria-expanded={whatWeDoAccordionOpen}
                  aria-controls="site-menu-what-we-do-panel"
                  className="flex w-full max-w-full cursor-pointer items-center justify-between gap-3 text-left text-2xl font-medium uppercase tracking-tight text-white/95 transition-colors hover:text-[#ff5c00] sm:text-4xl md:text-5xl lg:text-6xl"
                  onClick={() => setWhatWeDoAccordionOpen((o) => !o)}
                >
                  <span className="min-w-0">What we do</span>
                  <span
                    className={`shrink-0 text-lg leading-none text-white/70 transition-transform duration-200 sm:text-2xl md:text-3xl ${
                      whatWeDoAccordionOpen ? "rotate-180" : "rotate-0"
                    }`}
                    aria-hidden
                  >
                    ▼
                  </span>
                </button>
                <div
                  id="site-menu-what-we-do-panel"
                  role="region"
                  aria-labelledby="site-menu-what-we-do-trigger"
                  className={`grid w-full transition-[grid-template-rows] duration-300 ease-out ${
                    whatWeDoAccordionOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="min-h-0 overflow-hidden">
                    <ul className="mt-2 flex flex-col gap-2 border-l border-white/15 pl-4 sm:mt-3 sm:gap-3 sm:pl-5">
                      {WHAT_WE_DO_SUBMENU.map((sub) => (
                        <li key={sub.href}>
                          <Link
                            href={sub.href}
                            tabIndex={whatWeDoAccordionOpen ? undefined : -1}
                            className="block text-base font-medium uppercase tracking-tight text-white/80 transition-colors hover:text-[#ff5c00] sm:text-xl md:text-2xl"
                            onClick={closeMenu}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              {MENU_LINKS.filter((item) => !item.mobileOnly).map((item) => (
                <Link
                  key={item.label}
                  data-menu-link
                  href={item.href.startsWith("/") ? item.href : hashHref(pathname, item.href)}
                  className="block w-max max-w-full whitespace-normal text-2xl font-medium uppercase tracking-tight text-white/95 transition-colors hover:text-[#ff5c00] sm:whitespace-nowrap sm:text-4xl md:text-5xl lg:text-6xl"
                  onClick={(e) => {
                    if (!item.href.startsWith("/")) {
                      handleSectionLinkClick(e, item.href);
                    }
                    closeMenu();
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>,
          document.body
        )
      : null}
    </>
  );
}
